import React from 'react';
import { useSelector } from 'react-redux';

import { getCurrency, getL10nMoney, getMarket } from '@travel/i18n';
import { ArrowRight, CouponFilled } from '@travel/icons/ui';
import { cx } from '@travel/utils';

import { Translate } from 'core/translate';
import { CombinedIncentive } from 'ProviderRatePlan-Types';

import { getIsGlobalCurrency } from 'store/markets/selectors';

import styles from './couponBadge.module.scss';

type Props = {
  className?: string;
  /** Callback when the component has been clicked */
  onClick?: () => void;
  /** Combined Sales promotion incentive */
  combinedIncentive?: CombinedIncentive;
  /** Number of price after applied the coupon */
  priceAfterDiscount: number;
  /** Number of coupons acquired */
  noOfAppliedCoupons: number;
  /** Number of coupons applicable */
  noOfAvailableCoupons: number;
};

function CouponBadge(props: Props) {
  const { combinedIncentive, noOfAppliedCoupons, noOfAvailableCoupons } = props;

  const currency = useSelector(getCurrency);
  const isGlobalCurrency = useSelector(getIsGlobalCurrency);
  const market = useSelector(getMarket);

  if (combinedIncentive === undefined || (noOfAppliedCoupons === 0 && noOfAvailableCoupons === 0)) {
    return null;
  }

  const { totalDiscountIncentive, totalPointIncentive } = combinedIncentive;

  const isShowDetailLabel = noOfAvailableCoupons === 0;

  // if all coupons are applied discount amount is not shown
  const discountAmount = isShowDetailLabel ? null : totalDiscountIncentive &&
    totalPointIncentive ? (
    <Translate
      id="Provider_Info.Room_List.Plan_Info.Coupons.Amount_Point"
      data={{
        promotion_amount_exact: getL10nMoney(
          market.marketCode,
          totalDiscountIncentive,
          currency,
          isGlobalCurrency,
        ),
        exact_points: totalPointIncentive,
      }}
    />
  ) : totalDiscountIncentive ? (
    <Translate
      id="Provider_Info.Room_List.Plan_Info.Coupons.Amount_Off"
      data={{
        promotion_amount_exact: getL10nMoney(
          market.marketCode,
          totalDiscountIncentive,
          currency,
          isGlobalCurrency,
        ),
      }}
    />
  ) : totalPointIncentive ? (
    <Translate
      id="Provider_Info.Room_List.Plan_Info.Coupons.Point"
      data={{
        exact_points: totalPointIncentive,
      }}
    />
  ) : null;

  /**
   * there are at least 1 available => x coupons available
   * all are acquired => x coupons applied
   */
  const couponMessage =
    noOfAvailableCoupons > 0 ? (
      <Translate
        id="Provider_Info.Room_List.Plan_Info.Coupons.Available"
        count={noOfAppliedCoupons + noOfAvailableCoupons}
        data={{ coupon_count: noOfAppliedCoupons + noOfAvailableCoupons }}
      />
    ) : (
      <Translate
        id="Provider_Info.Room_List.Plan_Info.Coupons.Applied"
        count={noOfAppliedCoupons}
        data={{ coupon_count: noOfAppliedCoupons + noOfAvailableCoupons }}
      />
    );

  return (
    <button
      className={cx(props.className, styles.wrapper)}
      onClick={props.onClick}
      data-testid="providerInfo-ratePlanList-couponBadge-wrapper"
    >
      <div className={styles.text}>
        <div
          className={styles.subtitle}
          data-testid="providerInfo-ratePlanList-couponBadge-message"
        >
          <CouponFilled size={16} className={styles.couponIcon} />
          <div className={styles.couponMessage}>
            {couponMessage}
            &nbsp;{discountAmount}
          </div>
        </div>
      </div>
      <div className={styles.detail}>
        {isShowDetailLabel && (
          <div className={styles.detailMessage}>
            <Translate id="Provider_Info.Room_List.Plan_Info.Coupons.View_details" />
          </div>
        )}
        <ArrowRight className={styles.arrow} size={12} />
      </div>
    </button>
  );
}

export default CouponBadge;
